import { el, formatDuration, onTap } from './dom.ts';
import { iconEl } from './icons.ts';
import { api, type CatalogItem, type NowNext, type YoutubeItem } from '../api.ts';
import { toast } from './feedback.ts';
import { play } from '../playback.ts';
import { t } from '../i18n/index.ts';

// Kart ailesi. 10-TASARIM-V2: "Canlı kanal satırı ile film afişi aynı bileşen
// ölçüsünü paylaşmaz." Üç ayrı biçim var:
//   media   → 16:9 video/keşfet kartı
//   poster  → 2:3 film/dizi afişi
//   channel → 72–88 px yüksekliğinde canlı kanal SATIRI (kart değil)
//
// Görsel yoksa dev iki harfli kutu üretilmez:
//   video   → nötr görüntü simgesi + alt başlık
//   afiş    → başlığı taşıyan bilinçli tipografik kapak
//   kanal   → 44 px kompakt, okunaklı isim

function imageFrame(
  src: string | null | undefined,
  kind: 'video' | 'poster',
): HTMLElement {
  const frame = el('div', { class: 'frame' });
  // Afiş yoksa başlığı kartın İÇİNE de yazmıyoruz: hemen altında zaten
  // duruyordu ve her kart aynı metni iki kez gösteriyordu. Yer tutucu
  // sessiz bir yüzey; kimliği alttaki yazı taşır.
  const placeholder = () => (kind === 'poster'
    ? el('div', { class: 'ph ph-poster' }, el('span', { class: 'ph-ico' }, iconEl('film')))
    : el('div', { class: 'ph ph-video' }, iconEl('image'), el('span', {}, t('cards.noImage'))));
  if (src) {
    const img = el('img', { src, alt: '', loading: 'lazy', decoding: 'async' });
    // Kırık resim simgesi görünmemeli: yükleme başarısızsa yer tutucuya düş.
    // Yer tutucu resmin YERİNE geçer; sona eklenince favori düğmesinin üstüne
    // biniyor ve düğme tıklanamıyordu.
    img.addEventListener('error', () => { img.replaceWith(placeholder()); });
    frame.appendChild(img);
  } else {
    frame.appendChild(placeholder());
  }
  return frame;
}

interface FavInput { itemKey: string; kind: string; title: string; meta: unknown; on: boolean }

function favButton(input: FavInput, className = 'fav-btn'): HTMLElement {
  let on = input.on;
  let busy = false;
  const button = el('button', {
    class: className + (on ? ' on' : ''),
    type: 'button',
    'aria-label': on ? t('cards.removeFav') : t('cards.addFav'),
    'aria-pressed': on ? 'true' : 'false',
  });
  const paint = () => {
    button.replaceChildren(on ? iconEl('starFilled') : iconEl('star'));
    button.classList.toggle('on', on);
    button.setAttribute('aria-label', on ? t('cards.removeFav') : t('cards.addFav'));
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
  };
  paint();
  onTap(button, async (event) => {
    (event as Event).stopPropagation?.();
    (event as Event).preventDefault?.();
    // İstek sürerken ikinci dokunuş yok sayılır: iki istek birden gidince düğme
    // "kapalı" görünürken sunucuda "açık" kalıyordu.
    if (busy) return;
    busy = true;
    try {
      if (on) await api.del(`/api/library/favorites/${encodeURIComponent(input.itemKey)}`);
      else await api.post('/api/library/favorites', {
        itemKey: input.itemKey, kind: input.kind, title: input.title, meta: input.meta,
      });
      on = !on;
      paint();
    } catch {
      toast(t('cards.favFail'), 'error');
    } finally {
      busy = false;
    }
  });
  return button;
}

// --- YouTube / keşfet kartı (16:9) -------------------------------------------

export function youtubeCard(item: YoutubeItem, options: { onPlay?: () => void; favorite?: boolean } = {}): HTMLElement {
  const frame = imageFrame(item.thumbnail, 'video');
  if (item.live) frame.appendChild(el('span', { class: 'badge live' }, t('cards.live')));
  else if (item.duration) frame.appendChild(el('span', { class: 'badge' }, formatDuration(item.duration)));
  frame.appendChild(favButton({
    itemKey: `yt:${item.id}`,
    kind: item.live ? 'youtube-live' : 'youtube-vod',
    title: item.title,
    on: !!options.favorite,
    meta: { videoId: item.id, channel: item.channel, durationSec: item.duration, live: item.live, thumbnail: item.thumbnail },
  }));

  const card = el('button', { class: 'media', type: 'button' },
    frame,
    el('b', { class: 'clamp2' }, item.title),
    el('p', { class: 'clamp1' }, item.channel || 'YouTube'),
  );
  onTap(card, () => {
    options.onPlay?.();
    void play({
      source: { type: 'youtube', videoId: item.id, live: item.live, title: item.title, channel: item.channel, durationSec: item.duration },
      title: item.title,
      subtitle: item.channel,
      relatedQuery: { exclude: item.id, channel: item.channel, title: item.title },
    });
  });
  return card;
}

// --- Film / dizi afişi (2:3) --------------------------------------------------

export function posterCard(item: CatalogItem, options: { onOpen?: (item: CatalogItem) => void } = {}): HTMLElement {
  const frame = imageFrame(item.logo, 'poster');
  frame.appendChild(favButton({
    itemKey: item.itemKey, kind: `iptv-${item.kind}`, title: item.title, on: item.favorite,
    meta: { itemId: item.id, kind: item.kind, logo: item.logo },
  }));

  const card = el('button', { class: 'media poster', type: 'button' },
    frame,
    el('b', { class: 'clamp2' }, item.title),
    item.category ? el('p', { class: 'clamp1' }, item.category) : null,
  );
  onTap(card, () => {
    if (item.kind === 'series') { options.onOpen?.(item); return; }
    void play({ source: { type: 'iptv', itemId: item.id }, title: item.title, subtitle: item.category });
  });
  return card;
}

// --- Canlı kanal satırı -------------------------------------------------------

export interface ChannelRowInput {
  itemKey: string;
  kind: string;
  title: string;
  logo?: string | null;
  /** Kanal numarası ya da kısa işaret. Dev iki harfli kutu değil, 44 px kare. */
  mark?: string;
  /** EPG yokken satırı taranabilir kılan kısa bilgi (ör. kategori). */
  subtitle?: string | null;
  epg?: NowNext | null;
  /** Kaynak program bilgisi veriyorsa true; hazır kanallarda EPG yok, o yüzden
   *  "Program bilgisi yok" gibi boş bir satır da yazmıyoruz. */
  expectsEpg?: boolean;
  favorite: boolean;
  meta: unknown;
  onPlay: () => void;
}

export function channelRow(input: ChannelRowInput): HTMLElement {
  const mark = el('span', { class: 'channel-mark', 'aria-hidden': 'true' });
  if (input.logo) {
    const img = el('img', { src: input.logo, alt: '', loading: 'lazy' });
    img.addEventListener('error', () => { img.remove(); paintMark(mark, compactMark(input.mark)); });
    mark.appendChild(img);
  } else {
    paintMark(mark, compactMark(input.mark));
  }
  if (!input.logo && !mark.textContent) mark.classList.add('is-empty');

  const body = el('div', { class: 'channel-body' },
    el('b', { class: 'clamp1' }, input.title),
    input.epg?.now
      ? el('p', { class: 'clamp1' }, input.epg.now.title)
      : input.expectsEpg ? el('p', { class: 'clamp1 next' }, t('cards.noEpg'))
        : input.subtitle ? el('p', { class: 'clamp1 next' }, input.subtitle) : null,
    input.epg?.next ? el('p', { class: 'clamp1 next' }, t('cards.next', { title: input.epg.next.title })) : null,
  );

  const row = el('button', { class: 'channel-row', type: 'button' },
    mark,
    body,
    favButton({ itemKey: input.itemKey, kind: input.kind, title: input.title, on: input.favorite, meta: input.meta }, 'fav-inline'),
  );
  onTap(row, () => input.onPlay());
  return row;
}

/**
 * Kanal işareti. Yayıncı logosu kullanamıyoruz (telifli) — o yüzden burada
 * tek elimizdeki şey tipografi. İki hanelik numarada baştaki sıfır soluk
 * kalıyor: sayı bir yer tutucu gibi değil, kanal sırası gibi okunuyor.
 */
function paintMark(host: HTMLElement, text: string): void {
  const padded = /^0\d$/.test(text);
  if (padded) {
    host.replaceChildren(el('i', {}, text[0]), document.createTextNode(text.slice(1)));
    host.classList.add('is-num');
    return;
  }
  host.classList.toggle('is-num', /^\d+$/.test(text));
  host.textContent = text;
}

/**
 * İşaretin içeriği. Yalnızca GERÇEK numara gösterilir (hazır kanallarda
 * listeden, kendi listende sağlayıcının verdiği `tvg-chno`). Numara yoksa
 * işaret boş kalır; kanal adı zaten hemen yanında duruyor.
 */
function compactMark(explicit?: string): string {
  return explicit && /^\d+$/.test(explicit.replace(/^0+/, '') || '0') ? explicit : '';
}

// --- Devam et -----------------------------------------------------------------

export interface ContinueEntry {
  itemKey: string;
  kind: string;
  title: string;
  positionSec: number;
  durationSec: number;
  meta: Record<string, any>;
}

export function continueCard(entry: ContinueEntry): HTMLElement {
  const ratio = entry.durationSec > 0 ? entry.positionSec / entry.durationSec : 0;
  const thumbnail = thumbFor(entry.meta) ?? thumbFromKey(entry.itemKey);
  const remaining = entry.durationSec > 0 ? entry.durationSec - entry.positionSec : 0;
  const frame = imageFrame(thumbnail, 'video');
  frame.appendChild(el('span', { class: 'badge' },
    remaining > 0 ? t('cards.left', { time: formatDuration(remaining) }) : formatDuration(entry.positionSec)));
  if (ratio > 0) frame.appendChild(el('i', { class: 'progress-line', style: { width: `${Math.min(100, ratio * 100)}%` } }));

  const card = el('button', { class: 'media', type: 'button' },
    frame,
    el('b', { class: 'clamp2' }, entry.title),
    el('p', {}, kindLabel(entry.kind)),
  );
  onTap(card, () => void play(targetFromKey(entry)));
  return card;
}

export function favoriteCard(fav: { itemKey: string; kind: string; title: string; meta: Record<string, any> }): HTMLElement {
  const isPoster = fav.kind === 'iptv-movie' || fav.kind === 'iptv-series' || fav.kind === 'iptv-episode';
  const frame = imageFrame(thumbFor(fav.meta), isPoster ? 'poster' : 'video');
  if (isLiveKind(fav.kind)) frame.appendChild(el('span', { class: 'badge live' }, t('cards.live')));
  frame.appendChild(favButton({ itemKey: fav.itemKey, kind: fav.kind, title: fav.title, on: true, meta: fav.meta }));

  const card = el('button', { class: 'media' + (isPoster ? ' poster' : ''), type: 'button' },
    frame,
    el('b', { class: 'clamp2' }, fav.title),
    el('p', {}, kindLabel(fav.kind)),
  );
  onTap(card, () => void play(targetFromKey(fav)));
  return card;
}

function isLiveKind(kind: string): boolean {
  return kind === 'tv' || kind === 'iptv-live' || kind === 'youtube-live';
}

/** Meta yoksa bile YouTube anahtarından (yt:VIDEOID) görsel türetilebilir. */
export function thumbFromKey(itemKey: string): string | null {
  const m = /^yt:([\w-]{11})$/.exec(itemKey);
  return m ? `https://i.ytimg.com/vi/${m[1]}/mqdefault.jpg` : null;
}

export function thumbFor(meta: Record<string, any> | undefined): string | null {
  if (!meta) return null;
  if (typeof meta.thumbnail === 'string') return meta.thumbnail;
  if (typeof meta.videoId === 'string') return `https://i.ytimg.com/vi/${meta.videoId}/mqdefault.jpg`;
  if (typeof meta.logo === 'string') return meta.logo;
  return null;
}

export function kindLabel(kind: string): string {
  switch (kind) {
    case 'youtube-vod': return t('cards.kind.youtubeVod');
    case 'youtube-live': return t('cards.kind.youtubeLive');
    case 'tv': return t('cards.kind.tv');
    case 'iptv-live': return t('cards.kind.iptvLive');
    case 'iptv-movie': return t('cards.kind.iptvMovie');
    case 'iptv-episode': return t('cards.kind.iptvEpisode');
    case 'iptv-series': return t('cards.kind.iptvSeries');
    case 'test': return t('cards.kind.test');
    default: return t('cards.kind.content');
  }
}

export function targetFromKey(entry: { itemKey: string; title: string; meta: Record<string, any>; kind?: string }) {
  const [prefix, ...rest] = entry.itemKey.split(':');
  const id = rest.join(':');
  if (prefix === 'yt') {
    return {
      source: {
        type: 'youtube' as const, videoId: id, live: !!entry.meta.live,
        title: entry.title, channel: entry.meta.channel ?? '', durationSec: entry.meta.durationSec ?? null,
      },
      title: entry.title,
      relatedQuery: { exclude: id, channel: entry.meta.channel ?? '', title: entry.title },
    };
  }
  if (prefix === 'tv') return { source: { type: 'tv' as const, channelId: id }, title: entry.title };
  return { source: { type: 'iptv' as const, itemId: id }, title: entry.title };
}

// --- Dizi bölümü satırı -------------------------------------------------------

export function episodeRow(item: CatalogItem, showTitle: string): HTMLElement {
  const plot = typeof item.extra.plot === 'string' ? item.extra.plot : '';
  const duration = typeof item.extra.duration === 'number' ? item.extra.duration : null;
  // Görsel yoksa boş bir kutu koymuyoruz: üst üste gelen gri dikdörtgenler
  // satırı ağırlaştırıyor. Eksik veri gizlenir (10-TASARIM-V2).
  let shot: HTMLElement | null = null;
  if (item.logo) {
    shot = el('div', { class: 'shot' });
    const img = el('img', { src: item.logo, alt: '', loading: 'lazy' });
    const holder = shot;
    img.addEventListener('error', () => { holder.remove(); });
    shot.appendChild(img);
  }

  // "Kaldığınız yerden devam" vaadinin dizi detayındaki karşılığı: bölüm
  // satırında ince bir ilerleme çizgisi ve kalan süre.
  const seen = item.progress as { positionSec?: number; durationSec?: number } | undefined;
  const total = seen?.durationSec && seen.durationSec > 0 ? seen.durationSec : duration;
  const ratio = seen?.positionSec && total ? Math.min(1, seen.positionSec / total) : 0;
  const left = ratio > 0 && total ? Math.max(0, total - (seen!.positionSec ?? 0)) : 0;

  const meta: Array<string> = [];
  if (duration) meta.push(formatDuration(duration));
  if (ratio > 0) meta.push(left > 30 ? t('cards.left', { time: formatDuration(left) }) : t('cards.watched'));

  const row = el('button', { class: 'episode-row' + (shot ? '' : ' no-shot'), type: 'button' },
    el('span', { class: 'episode-play', 'aria-hidden': 'true' }, iconEl('play')),
    shot,
    el('div', { class: 'grow' },
      el('b', {}, `${item.episode ?? '?'}. ${item.title}`),
      // Eksik veri gizlenir; boş satır bırakılmaz.
      plot ? el('p', { class: 'clamp2' }, plot) : null,
      meta.length ? el('p', { class: 'episode-meta' }, meta.join(' · ')) : null,
      ratio > 0 ? el('div', { class: 'episode-bar' },
        el('i', { style: { width: `${Math.round(ratio * 100)}%` } })) : null,
    ),
  );
  onTap(row, () => void play({
    source: { type: 'iptv', itemId: item.id },
    title: `${showTitle} — ${item.title}`,
    subtitle: t('cards.episodeSub', { season: item.season ?? 1, episode: item.episode ?? '' }),
  }));
  return row;
}
